import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  LayoutDashboard,
  FileText,
  Calendar as CalendarIcon,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Plus,
  Clock,
  MapPin,
  Video,
  Users,
  FileCheck,
  Edit,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsModule = () => {
  const { consortiumId } = useParams();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    scheduled_at: new Date(),
    location: "",
    is_virtual: false,
    meeting_link: "",
    agenda_items: "",
  });

  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        { name: "Overview", path: `/consortium/${consortiumId}`, icon: LayoutDashboard },
        { name: "Agreements", path: `/consortium/${consortiumId}/agreements`, icon: FileText },
        { name: "Meetings", path: `/consortium/${consortiumId}/meetings`, icon: CalendarIcon },
        { name: "Voting", path: `/consortium/${consortiumId}/voting`, icon: Vote },
        { name: "RFPs & Bids", path: `/consortium/${consortiumId}/rfps`, icon: FileSpreadsheet },
        { name: "Consent", path: `/consortium/${consortiumId}/consent`, icon: CheckSquare },
      ],
    },
  ];

  useEffect(() => {
    fetchMeetings();
  }, [consortiumId]);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings?consortium_id=${consortiumId}`);
      setMeetings(response.data);
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      const meetingData = {
        consortium_id: consortiumId,
        title: formData.title,
        description: formData.description,
        scheduled_at: selectedDate.toISOString(),
        location: formData.location,
        is_virtual: formData.is_virtual,
        meeting_link: formData.meeting_link,
        agenda_items: formData.agenda_items.split("\n").filter((item) => item.trim()),
      };

      await axios.post(`${API}/meetings`, meetingData);
      toast.success("Meeting scheduled successfully!");
      setDialogOpen(false);
      setFormData({
        title: "",
        description: "",
        scheduled_at: new Date(),
        location: "",
        is_virtual: false,
        meeting_link: "",
        agenda_items: "",
      });
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to schedule meeting");
    }
  };

  const handleAttend = async (meetingId) => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/attend`);
      toast.success("Attendance marked!");
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to mark attendance");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-[#4E80FF]";
      case "ongoing":
        return "bg-blue-100 text-[#4E80FF]";
      case "completed":
        return "bg-slate-100 text-slate-600";
      case "cancelled":
        return "bg-red-100 text-red-600";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="meetings-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Meetings & Administration
            </h1>
            <p className="text-slate-600">
              Schedule and manage consortium meetings
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="schedule-meeting-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Schedule New Meeting</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateMeeting} className="space-y-4">
                <div className="space-y-2">
                  <Label>Meeting Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g., Monthly Review Meeting"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date & Time</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {format(selectedDate, "PPP p")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_virtual}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_virtual: checked })
                      }
                    />
                    <Label>Virtual Meeting</Label>
                  </div>
                </div>

                {formData.is_virtual ? (
                  <div className="space-y-2">
                    <Label>Meeting Link</Label>
                    <Input
                      value={formData.meeting_link}
                      onChange={(e) =>
                        setFormData({ ...formData, meeting_link: e.target.value })
                      }
                      placeholder="https://meet.google.com/..."
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      placeholder="Meeting venue address"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Agenda Items (one per line)</Label>
                  <Textarea
                    value={formData.agenda_items}
                    onChange={(e) =>
                      setFormData({ ...formData, agenda_items: e.target.value })
                    }
                    placeholder="1. Review previous minutes&#10;2. Land pooling status update&#10;3. Developer proposals"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Additional details about the meeting"
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Schedule Meeting
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
          </div>
        ) : meetings.length > 0 ? (
          <div className="space-y-4">
            {meetings.map((meeting) => (
              <Card key={meeting.id} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        {meeting.is_virtual ? (
                          <Video className="w-6 h-6 text-[#4E80FF]" />
                        ) : (
                          <MapPin className="w-6 h-6 text-[#4E80FF]" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900">
                          {meeting.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(meeting.scheduled_at).toLocaleString()}
                          </span>
                          {meeting.is_virtual ? (
                            <span className="flex items-center gap-1">
                              <Video className="w-4 h-4" />
                              Virtual
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {meeting.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge className={getStatusColor(meeting.status)}>
                      {meeting.status}
                    </Badge>
                  </div>

                  {meeting.agenda_items?.length > 0 && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700 mb-2">Agenda:</p>
                      <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                        {meeting.agenda_items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Users className="w-4 h-4" />
                      {meeting.attendees?.length || 0} attendees
                    </div>
                    <div className="flex gap-2">
                      {meeting.status === "scheduled" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAttend(meeting.id)}
                        >
                          <CheckSquare className="w-4 h-4 mr-1" />
                          Mark Attendance
                        </Button>
                      )}
                      {meeting.status === "completed" && meeting.minutes && (
                        <Button variant="outline" size="sm">
                          <FileCheck className="w-4 h-4 mr-1" />
                          View Minutes
                        </Button>
                      )}
                      <Button variant="outline" size="sm">
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                No Meetings Scheduled
              </h3>
              <p className="text-slate-500 mb-6">
                Schedule your first consortium meeting to coordinate with members.
              </p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                Schedule Meeting
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MeetingsModule;
